import React, { useRef } from 'react';
import { useRemountOnPageHidden } from '../../hooks/useRemountOnPageHidden';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonButton,
  IonItem,
  IonLabel
} from '@ionic/react';
import {
  people,
  calendar,
  statsChart,
  briefcase,
  checkmarkCircle,
  alertCircle,
  time
} from 'ionicons/icons';
import './Dashboard.module.scss';
import { useTranslation } from 'react-i18next';

const DashboardManager: React.FC = () => {
  const { t } = useTranslation();
  // The scrolling area is rebuilt while the page is hidden, so it is back at the top on return
  const scrollAreaRef = useRef<HTMLIonContentElement>(null);
  const scrollResetKey = useRemountOnPageHidden(scrollAreaRef);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('pages.dashboard.DashboardManager.managerDashboard')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent key={scrollResetKey} ref={scrollAreaRef} fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{t('pages.dashboard.DashboardManager.managerDashboard')}</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="dashboard-container">
          {/* Quick Stats */}
          <IonGrid>
            <IonRow>
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3>{t('pages.dashboard.DashboardManager.todaySBookings')}</h3>
                        <h2>12</h2>
                      </div>
                      <IonIcon icon={calendar} size="large" color="primary" />
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
              
              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3>{t('pages.dashboard.DashboardManager.activeStaff')}</h3>
                        <h2>8</h2>
                      </div>
                      <IonIcon icon={people} size="large" color="success" />
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>

              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3>{t('pages.dashboard.DashboardManager.completedJobs')}</h3>
                        <h2>45</h2>
                      </div>
                      <IonIcon icon={checkmarkCircle} size="large" color="success" />
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>

              <IonCol size="12" sizeMd="6" sizeLg="3">
                <IonCard>
                  <IonCardContent>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <h3>{t('pages.dashboard.DashboardManager.pendingReviews')}</h3>
                        <h2>3</h2>
                      </div>
                      <IonIcon icon={alertCircle} size="large" color="warning" />
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>

          {/* Management Actions */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('pages.dashboard.DashboardManager.managementActions')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid>
                <IonRow>
                  <IonCol size="12" sizeMd="6">
                    <IonButton expand="block" fill="outline" routerLink="/tabs/staff">
                      <IonIcon icon={people} slot="start" />{t('pages.dashboard.DashboardManager.manageStaff')}
                    </IonButton>
                  </IonCol>
                  <IonCol size="12" sizeMd="6">
                    <IonButton expand="block" fill="outline" routerLink="/tabs/schedule">
                      <IonIcon icon={calendar} slot="start" />{t('pages.dashboard.DashboardManager.scheduleManagement')}
                    </IonButton>
                  </IonCol>
                  <IonCol size="12" sizeMd="6">
                    <IonButton expand="block" fill="outline" routerLink="/tabs/reports">
                      <IonIcon icon={statsChart} slot="start" />{t('pages.dashboard.DashboardManager.viewReports')}
                    </IonButton>
                  </IonCol>
                  <IonCol size="12" sizeMd="6">
                    <IonButton expand="block" fill="outline" routerLink="/tabs/services">
                      <IonIcon icon={briefcase} slot="start" />{t('pages.dashboard.DashboardManager.serviceManagement')}
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>

          {/* Recent Activities */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('pages.dashboard.DashboardManager.recentActivities')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonIcon icon={checkmarkCircle} slot="start" color="success" />
                <IonLabel>
                  <h3>{t('pages.dashboard.DashboardManager.booking1234Completed')}</h3>
                  <p>{t('pages.dashboard.DashboardManager.residentialCleaningJohnsonResidence')}</p>
                </IonLabel>
                <IonIcon icon={time} slot="end" />
              </IonItem>
              <IonItem>
                <IonIcon icon={people} slot="start" color="primary" />
                <IonLabel>
                  <h3>{t('pages.dashboard.DashboardManager.newStaffMemberAdded')}</h3>
                  <p>{t('pages.dashboard.DashboardManager.sarahMillerJoinedTheTeam')}</p>
                </IonLabel>
                <IonIcon icon={time} slot="end" />
              </IonItem>
              <IonItem>
                <IonIcon icon={alertCircle} slot="start" color="warning" />
                <IonLabel>
                  <h3>{t('pages.dashboard.DashboardManager.scheduleConflict')}</h3>
                  <p>{t('pages.dashboard.DashboardManager.overlappingBookingsNeedReview')}</p>
                </IonLabel>
                <IonIcon icon={time} slot="end" />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {/* Quick Actions */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{t('pages.dashboard.DashboardManager.quickActions')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonButton expand="block" routerLink="/booking/new">{t('pages.dashboard.DashboardManager.createNewBooking')}
              </IonButton>
              <IonButton expand="block" fill="outline" routerLink={import.meta.env.VITE_PLATFORM === 'desktop' ? '/tabs/schedule' : '/tabs/calendar'}>{t('pages.dashboard.DashboardManager.viewFullCalendar')}
              </IonButton>
              <IonButton expand="block" fill="clear" routerLink="/tabs/inbox">{t('pages.dashboard.DashboardManager.checkMessages')}
              </IonButton>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default DashboardManager;
